import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import "./topBar.scss"; // owns .tb-user / .tb-user-menu — the dashboard has no TopBar
import AuthenticationService from "service/auth/AuthenticationService";
import usePopover from "components/common/usePopover";

/**
 * The signed-in user's avatar + name, and the menu it opens (Figma 3015:8489,
 * "Menu · Account (opens from name tag)"): the user's DOCTOR CODE (a chip with a
 * copy button) and PHONE above a divider, then Log out (2026-09-17 — it used to
 * be Log out alone).
 *
 * Both details come from the signed-in profile the login stored — no request:
 * `doctorProfile.code` (api-user-service doc_profile.code) and the phone with
 * its country code, from the doctor profile, else the employee profile, else
 * the account. A detail that is not on the profile is not drawn, and with
 * neither the menu is Log out alone.
 *
 * Lives here rather than inside TopBar because TopBar is only on four screens —
 * and NOT on the appointments dashboard, which is where a user lands after
 * logging in and spends most of their time. Shipping the menu in TopBar alone
 * would mean you cannot log out from the main screen.
 *
 * NO ROLE SUBTITLE. The design (3015:8489) draws one, and this used to ship
 * it as the literal string "Care Manager" for EVERY user — so a doctor was
 * told they were a care manager. A real one is reachable (GET
 * /userroles/{userId}, which bridge declares), but it costs a request per
 * session to print a job title beside someone's own name, and bridge dropped
 * its equivalent for that reason on 2026-08-27. Dropped here rather than made
 * real.
 */

/** Figma's icon/log-out, 17x15 (3015:8489). */
const LogOutIcon = () => (
  <svg
    className="tb-menu-icon"
    viewBox="0 0 17 15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9.5 1H3C1.9 1 1 1.9 1 3V12C1 13.1 1.9 14 3 14H9.5" />
    <path d="M7.5 7.5H15.5M12.7 11L15.5 7.5L12.7 4" />
  </svg>
);

/** Material "content_copy" / "check", drawn at the board's 14px. */
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

/** "+91 98765 43210" — a ten-digit number grouped 5 + 5, as the board draws it. */
export const formatPhone = (countryCode, phone) => {
  const digits = String(phone == null ? "" : phone).replace(/\D/g, "");
  if (!digits || /^0+$/.test(digits)) return "";
  const body = digits.length === 10 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;
  const cc = String(countryCode == null ? "" : countryCode).replace(/\D/g, "");
  return cc ? `+${cc} ${body}` : body;
};

/** The two details the menu shows, from the stored login profile. */
export const accountDetails = (profile) => {
  const p = profile || {};
  const doc = p.doctorProfile || {};
  const emp = p.employeeProfile || {};
  const code = String(doc.code || "").trim();
  const src = [doc, emp, p].find((x) => formatPhone(null, x.phoneno)) || {};
  const phone = formatPhone(src.countryCode || p.countryCode || 91, src.phoneno);
  return { code, phone };
};

/** Copies text, with the textarea fallback for a context without the async API. */
const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) resolve();
    else reject(new Error("copy failed"));
  });
};

const MENU_WIDTH = 248;

const UserMenu = () => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, () => setOpen(false));

  // One instance, reused: constructing AuthenticationService re-runs
  // setDefaultCookies(), which rewrites the `client` cookie on every render.
  const [auth] = useState(() => new AuthenticationService());
  /* The user's FULL name. It used to run through abbreviateName(), which cut
     "Nimit Badak" to "Nimit B." — a top-bar space saving that stopped being
     worth it once the role subtitle below it went: the chip is one line now and
     has the width. `.tb-user-name` keeps `white-space: nowrap`, so a genuinely
     long name still cannot wrap the bar.

     READ ONCE, at mount. logout() empties storage and THEN navigates, and the
     page stays up until the next one loads — the menu closing re-rendered this
     chip in between, re-read an empty profile, and painted getUserName()'s
     "User" fallback with no avatar for a moment (2026-09-21). Nothing changes
     who is signed in without a full page load (Login writes the profile, then
     loads), so the snapshot is exact. */
  const [{ name, image, code, phone }] = useState(() => ({
    name: auth.getUserName(),
    image: auth.getUserProfileImage(),
    ...accountDetails(auth.getUserProfile()),
  }));

  /* "Copied" for a moment after the copy button is pressed — the glyph turns
     into a check and the button says so to assistive tech. */
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);
  const copyCode = () => {
    copyText(code).then(
      () => {
        setCopied(true);
        clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopied(false), 1500);
      },
      () => {}
    );
  };

  /**
   * `logout()` ends in a hard `window.location.href = "/"`, and that reload is
   * load-bearing — two module-level singletons (newAPIService's instance, with
   * the previous user's auth header baked into `this.headers`, and
   * ReferenceListService's `cache`) are only torn down by a real navigation.
   * Never "improve" this into a router push.
   */
  const logout = () => {
    setOpen(false);
    auth.logout();
  };

  /**
   * usePopover left-aligns to the trigger and has no right-align mode. This
   * trigger sits at the far right, so a menu wider than it would be dragged
   * back by the viewport clamp to 8px from the edge — while the bar it hangs
   * from has 24px of padding. Right-align it to the trigger instead, which is
   * what the board draws.
   */
  const style =
    menuStyle.left != null && triggerRef.current
      ? {
          top: menuStyle.top,
          left: Math.max(8, triggerRef.current.getBoundingClientRect().right - MENU_WIDTH),
        }
      : { top: menuStyle.top, left: menuStyle.left };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="tb-user"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <img className="tb-avatar" src={image} alt="" />
        {/* The name alone — .tb-user-text's only job was stacking it above the
            role, so the wrapper went with it. */}
        <span className="tb-user-name">{name}</span>
      </button>

      {open &&
        ReactDOM.createPortal(
          <div ref={menuRef} className="tb-user-menu" style={style} role="menu" aria-label="Account">
            <div className="tb-menu-divider" aria-hidden="true" />
            {code || phone ? (
              <>
                <div className="tb-menu-details">
                  {code ? (
                    <div className="tb-menu-field">
                      <span className="tb-menu-label" id="tb-menu-code-label">Doctor code</span>
                      <span className="tb-menu-value-row">
                        <span className="tb-menu-chip" aria-labelledby="tb-menu-code-label">{code}</span>
                        <button
                          type="button"
                          className={`tb-menu-copy${copied ? " is-copied" : ""}`}
                          aria-label={copied ? "Doctor code copied" : "Copy doctor code"}
                          title={copied ? "Copied" : "Copy doctor code"}
                          onClick={copyCode}
                        >
                          {copied ? <CheckIcon /> : <CopyIcon />}
                        </button>
                      </span>
                    </div>
                  ) : null}
                  {phone ? (
                    <div className="tb-menu-field is-tight">
                      <span className="tb-menu-label">Phone</span>
                      <span className="tb-menu-value">{phone}</span>
                    </div>
                  ) : null}
                </div>
                <div className="tb-menu-divider" aria-hidden="true" />
              </>
            ) : null}
            <div className="tb-menu-actions">
              <button type="button" className="tb-menu-item is-danger" role="menuitem" onClick={logout}>
                <LogOutIcon />
                Log out
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default UserMenu;
